import { formatDate } from '@/helpers/date';
import { formatNumberWithSuffix } from '@uikit/helpers/formatNumber';
import { CourseMember } from '../CourseFormMembers/types';
import { CourseRegistrationData, CourseRequestFormData } from './types';

const courseRequestFormKeysOrder = [
  'Kurs',
  'Kurslink',
  'Thema',
  'firstName',
  'lastName',
  'mail',
  'Telefon',
  'Personenanzahl',
  'Wunschtermin',
];

export const createCourseRequestSubmitFormData = ({
  title,
  courseLink,
  categoryTitle,
  firstName,
  lastName,
  email,
  phone,
  memberAmount,
  desiredDate,
}: CourseRequestFormData) => {
  return {
    Kurs: title,
    Kurslink: courseLink,
    Thema: categoryTitle,
    firstName,
    lastName,
    mail: email,
    Telefon: phone,
    Personenanzahl: memberAmount,
    Wunschtermin: desiredDate,

    keysOrder: courseRequestFormKeysOrder,
  } as const;
};

const createMemberKeyByIndex = (index: number) =>
  `Teilnehmer:innen-${index + 1}`;

const membersToObject = (members: CourseMember[]) => {
  return members?.reduce((result: Record<string, string>, member, index) => {
    result[createMemberKeyByIndex(index)] =
      `${member.firstName} ${member.lastName} (${member.email})`;

    return result;
  }, {});
};

export const createCourseRegistrationSubmitFormData = ({
  title,
  categoryTitle,
  price,
  designation,
  firstName,
  lastName,
  company,
  postalCode,
  city,
  street,
  streetNumber,
  email,
  phone,
  annotations,
  hasDifferentBillingAddress,
  billingEmail,
  billingFirstName,
  billingLastName,
  billingCompany,
  billingPostalCode,
  billingCity,
  billingStreet,
  billingStreetNumber,
  privacyAgreement,
  marketingAgreement,
  members,
  courseLink,
  currentEvent,
}: CourseRegistrationData) => {
  const data = {
    Kurs: title,
    Kurslink: courseLink,
    Thema: categoryTitle,
    Preis: `${price} €`,
    ...(currentEvent && {
      Startdatum: formatDate(currentEvent?.startDate),
      Enddatum: formatDate(currentEvent?.endDate),
    }),
    designation,
    firstName,
    lastName,
    Firma: company,
    PLZ: postalCode,
    city,
    street,
    streetNumber,
    mail: email,
    phone,
    Anmerkungen: annotations,
    ...(billingEmail ? { 'Rechnungs-E-Mail': billingEmail } : {}),
    ...(hasDifferentBillingAddress
      ? {
          'Rechnungs-Vorname': billingFirstName,
          'Rechnungs-Nachname': billingLastName,
          'Rechnungs-Firma': billingCompany,
          'Rechnungs-PLZ': billingPostalCode,
          'Rechnungs-Ort': billingCity,
          'Rechnungs-Straße': billingStreet,
          'Rechnungs-Hausnummer': billingStreetNumber,
        }
      : {}),
    Datenschutz: privacyAgreement ? 'Ja' : 'Nein',
    'Marketing Einwilligung': marketingAgreement ? 'Ja' : 'Nein',
    ...membersToObject(members),
  };

  const keysOrder = Array.from(
    new Set([
      'Kurs',
      'Kurslink',
      'Thema',
      'Preis',
      'Startdatum',
      'Enddatum',
      'designation',
      'firstName',
      'lastName',
      'PLZ',
      'city',
      'street',
      'streetNumber',
      'mail',
      'phone',
      'Anmerkungen',
      ...Object.keys(data),
    ])
  );

  return {
    ...data,
    keysOrder,
  } as const;
};

export const getMinMaxEventDays = <
  T extends { startDate: Date; endDate: Date },
>(
  events: T[] = [],
  initialAmount = 0
) => {
  return events.reduce(
    (acc, { startDate, endDate }) => {
      const days = Math.ceil(
        (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24)
      );

      return {
        minDays: acc.minDays === 0 || days < acc.minDays ? days : acc.minDays,
        maxDays: days > acc.maxDays ? days : acc.maxDays,
      };
    },
    {
      minDays: initialAmount,
      maxDays: initialAmount,
    }
  );
};

export const createdurationInfoFromEvents = <
  T extends { startDate: Date; endDate: Date },
>(
  events: T[] = [],
  minimumDaysAmount = 0
) => {
  const { minDays, maxDays } = getMinMaxEventDays(events, minimumDaysAmount);

  const formatDays = (days: number) =>
    formatNumberWithSuffix(days, 'Tag', 'Tage');

  if (minDays === maxDays) {
    return formatDays(minDays);
  }

  return `${minDays} bis ${formatDays(maxDays)}`;
};
